import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { TokenRoleCap } from "@kandoo/shared";
import { api } from "../api/client";
import { Avatar } from "../components/Avatar";

/**
 * OAuth consent: an app (claude.ai's Kandoo connector) is asking for access.
 * The backend's /oauth/authorize forwards here with the request's query
 * string; this page asks the API whether it's valid, lets the signed-in user
 * pick who the app acts as and how far it may go, and then sends the browser
 * back to the app with a code (or a refusal).
 *
 * It's a SPA page rather than a server-rendered one because the session cookie
 * is SameSite=Strict: it isn't sent on the cross-site hop from the app, but it
 * is on this page's own API calls.
 */
export function OAuthConsentPage() {
  const { search } = useLocation();
  const params = useMemo(() => Object.fromEntries(new URLSearchParams(search)), [search]);
  const info = useQuery({
    queryKey: ["oauth-consent", search],
    queryFn: () => api.oauth.consentInfo(search),
    retry: false,
  });
  const [actAs, setActAs] = useState<string>("");
  const [roleCap, setRoleCap] = useState<TokenRoleCap | "">("editor");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default to the first service user: an agent's comments and activity then
  // read as the bot's, which is how agents work on this server. Falls back to
  // the user themselves when they have none.
  useEffect(() => {
    if (!info.data || actAs) return;
    const ids = info.data.identities;
    setActAs((ids.find((i) => i.kind === "service") ?? ids[0]).id);
  }, [info.data, actAs]);

  async function answer(approve: boolean) {
    setBusy(true);
    setError(null);
    try {
      const { redirect } = await api.oauth.consent({ params, approve, actAs, roleCap });
      window.location.assign(redirect);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  const appName = info.data?.client.name ?? "An app";
  // A request that's invalid but names a known client and redirect URI can be
  // handed back to that client with the error, as OAuth expects.
  const errorRedirect = (info.error as { body?: { redirect?: string } } | null)?.body?.redirect;

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-5 rounded-lg border border-slate-200 bg-[var(--surface)] p-6 shadow-sm">
        {info.isLoading && <p className="text-slate-500">Checking the request…</p>}

        {info.error && (
          <>
            <h1 className="text-xl font-semibold">Can't connect this app</h1>
            <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {(info.error as Error).message}
            </div>
            {errorRedirect && (
              <button
                onClick={() => window.location.assign(errorRedirect)}
                className="w-full rounded border border-slate-300 px-4 py-2 hover:bg-slate-50"
              >
                Return to the app
              </button>
            )}
          </>
        )}

        {info.data && (
          <>
            <div>
              <h1 className="text-xl font-semibold">Connect {appName} to Kandoo</h1>
              <p className="mt-1 text-sm text-slate-600">
                {appName} is asking to read and change boards on your behalf. After you
                answer, you'll be sent back to{" "}
                <span className="font-medium text-slate-800">{info.data.redirectHost}</span>.
              </p>
            </div>

            {error && (
              <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm text-slate-600">Act as</legend>
              {info.data.identities.map((i) => (
                <label
                  key={i.id}
                  className={`flex cursor-pointer items-center gap-3 rounded border px-3 py-2 ${
                    actAs === i.id ? "border-blue-500 bg-blue-50" : "border-slate-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="actAs"
                    value={i.id}
                    checked={actAs === i.id}
                    onChange={() => setActAs(i.id)}
                  />
                  <Avatar userId={i.id} name={i.displayName ?? i.username} avatar={i.avatar} size="md" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {i.displayName ?? i.username}
                      {i.kind === "self" && <span className="font-normal text-slate-500"> (you)</span>}
                    </span>
                    <span className="block text-xs text-slate-500">
                      @{i.username}
                      {i.kind === "service" && " · service user — comments and changes show as this bot"}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <label className="block text-sm">
              <span className="text-slate-600">Access</span>
              <select
                value={roleCap}
                onChange={(e) => setRoleCap(e.target.value as TokenRoleCap | "")}
                className="mt-1 block w-full rounded border border-slate-300 bg-[var(--surface)] px-2 py-2 text-sm"
              >
                <option value="">Full (your role on each board)</option>
                <option value="editor">Editor at most</option>
                <option value="viewer">Viewer (read-only)</option>
              </select>
            </label>

            <p className="text-xs text-slate-500">
              The connection appears on the API tokens page, where you can revoke it at any time.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => answer(false)}
                disabled={busy}
                className="flex-1 rounded border border-slate-300 px-4 py-2 hover:bg-slate-50 disabled:opacity-50"
              >
                Deny
              </button>
              <button
                onClick={() => answer(true)}
                disabled={busy || !actAs}
                className="flex-1 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? "Connecting…" : "Approve"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
